import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api';
import DocumentUpload from '../components/DocumentUpload';
import ScanSuggestionModal, { SCAN_SUGGESTION_CODES } from '../components/ScanSuggestionModal';
import UntranslatableModal from '../components/UntranslatableModal';
import ChatHistory from '../components/ChatHistory';
import { FileText, ScanLine, Shield, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './DashboardPage.css';

const RETENTION_HINT_KEY = 'cc_seen_retention_hint';

function DashboardPage() {
  const { t } = useTranslation('dashboard');
  const { user } = useAuth();
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [scanSuggestion, setScanSuggestion] = useState(null);
  const [untranslatableFile, setUntranslatableFile] = useState(null);
  // One-time retention hint: shown once per browser, dismissed forever once
  // the user closes it. Stays hidden if the user has already opted in.
  const [showRetentionHint, setShowRetentionHint] = useState(() => {
    try {
      return !localStorage.getItem(RETENTION_HINT_KEY);
    } catch {
      return false;
    }
  });
  const dismissRetentionHint = () => {
    setShowRetentionHint(false);
    try { localStorage.setItem(RETENTION_HINT_KEY, '1'); } catch {}
  };
  const navigate = useNavigate();

  useEffect(() => {
    loadChats();
  }, []);

  const loadChats = async () => {
    try {
      const data = await api.getChats();
      setChats(data);
    } catch (error) {
      console.error('Failed to load chats:', error);
    } finally {
      setLoading(false);
    }
  };

  // Scans live on their own /scans page - the dashboard shows document
  // translations only.
  const documentChats = useMemo(() => chats.filter((c) => !c.is_scanned), [chats]);

  const handleUploadStart = () => {
    setCreating(true);
  };

  const handleUpload = async (file, languageTo, languageVariant, context) => {
    try {
      const chat = await api.createChat(file.name);
      await api.uploadDocument(file, chat.id, languageTo, languageVariant, context);
      navigate(`/chat/${chat.id}`);
    } catch (error) {
      console.error('Failed to upload document:', error);
      const detail = error.response?.data?.detail;
      if (detail?.error_code === 'FILE_TOO_LARGE') {
        alert(t('errors.fileTooLarge', { maxMb: detail.params?.max_mb }));
      } else if (detail?.error_code === 'CHAR_LIMIT_REACHED') {
        // Already handled by global event in api.js
      } else if (detail?.error_code === 'MONTHLY_LIMIT_REACHED') {
        alert(t('errors.monthlyLimit'));
      } else if (SCAN_SUGGESTION_CODES.includes(detail?.error_code)) {
        setScanSuggestion(detail.error_code);
      } else if (detail?.error_code === 'UNTRANSLATABLE_CONTENT') {
        setUntranslatableFile(file);
      } else if (detail?.message) {
        alert(detail.message);
      } else {
        alert(t('errors.uploadFailed'));
      }
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="dashboard-loading">
        <div className="spinner" />
        <p>{t('loading')}</p>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <header className="dashboard-header">
        <div>
          <h1>{t('title')}</h1>
          <p>{t('subtitle')}</p>
        </div>
      </header>

      {showRetentionHint && !user?.auto_delete_days && (
        <div className="dashboard-retention-hint">
          <Shield size={16} />
          <div className="dashboard-retention-hint-text">
            <strong>{t('retentionHint.title')}</strong>{' '}
            <span>{t('retentionHint.body')} </span>
            <Link to="/settings">{t('retentionHint.cta')}</Link>
          </div>
          <button
            type="button"
            className="dashboard-retention-hint-close"
            onClick={dismissRetentionHint}
            title={t('retentionHint.dismiss')}
          >
            <X size={16} />
          </button>
        </div>
      )}

      <div className="dashboard-upload-section">
        <DocumentUpload
          onUpload={handleUpload}
          onUploadStart={handleUploadStart}
          disabled={creating}
        />
        <button
          className="btn btn-secondary scan-cta-btn"
          onClick={() => navigate('/scans')}
        >
          <ScanLine size={18} />
          {t('scans:cta.scanAndTranslate')}
        </button>
      </div>

      <ChatHistory
        chats={documentChats}
        onDeleted={(id) => setChats((prev) => prev.filter((c) => c.id !== id))}
        onRenamed={(id, name) => setChats((prev) => prev.map((c) => (c.id === id ? { ...c, name } : c)))}
        emptyIcon={FileText}
        emptyTitle={t('empty.noTranslations')}
        emptyBody={t('empty.uploadFirst')}
      />

      {/* Placeholder buttons */}
      <div className="coming-soon-section">
        <h3>{t('comingSoon.title')}</h3>
        <div className="coming-soon-buttons">
          <button className="btn btn-secondary" disabled title={t('comingSoon.title')}>
            {t('comingSoon.template')}
          </button>
          <button className="btn btn-secondary" disabled title={t('comingSoon.title')}>
            {t('comingSoon.notary')}
          </button>
        </div>
      </div>

      <ScanSuggestionModal
        isVisible={!!scanSuggestion}
        reason={scanSuggestion}
        onClose={() => setScanSuggestion(null)}
      />

      <UntranslatableModal
        isVisible={!!untranslatableFile}
        fileName={untranslatableFile?.name}
        onClose={() => setUntranslatableFile(null)}
      />
    </div>
  );
}

export default DashboardPage;
